// ─────────────────────────────────────────────────────────────
// RiskCauseAnalysis.js - Scandinavian Mantine Version
// Gentle chromatic design with consistent styling
// ─────────────────────────────────────────────────────────────

import React, { useMemo, useCallback } from 'react';
import { useSelector, useDispatch, useStore } from 'react-redux';
import {
  Container,
  Paper,
  Title,
  Text,
  Stack,
  Group,
  Badge,
  Alert,
} from '@mantine/core';
import {
  IconAlertCircle,
} from '@tabler/icons-react';
import { Navigation } from '../components/NavigationButtons';
import CauseTable from './CauseTable.js';

import {
  setRootCause,
  setDeepDiveValues,
  setDeepDiveDetail,
  saveCauses,
} from '../redux/actions/riskActions';

/**
 * Props
 * ─────
 * modelId, assessmentUuid, riskArea (sectionName),
 * source      – { name, … }  ← the card the user picked
 * onNext      – advance to analysis step
 */
export default function RiskCauseAnalysis({
  modelId,
  assessmentUuid,
  riskArea,
  source,
  onNext,
  onBack,
}) {
  const dispatch = useDispatch();
  const store = useStore();

  /* ── templates with isolation safeguards ─────────────────── */
  const { causesTemplate = {}, evalTemplate = {} } = useSelector((state) => {
    const sec = state.risks.models[modelId]?.riskFormData?.[riskArea] || {};
    const src = sec.riskSources?.find((r) => r.name === source.name) || {};
    
    console.log('[RiskCauseAnalysis] Debug selector:', {
      modelId,
      riskArea,
      sourceName: source.name,
      foundSource: !!src,
      hasTemplate: !!src.causesTemplate,
      templateKeys: src.causesTemplate ? Object.keys(src.causesTemplate) : [],
      allSourceNames: sec.riskSources?.map(s => s.name) || []
    });
    
    return {
      // Create isolated copies to prevent shared references
      causesTemplate: src.causesTemplate ? JSON.parse(JSON.stringify(src.causesTemplate)) : {},
      evalTemplate:
        state.config.configs['riskCauseEvaluation']?.defaultCriteria || {},
    };
  });

  /* ── existing answers with isolation safeguards ────── */
  const rawAnswers = useSelector(
    (s) => {
      const src = s.risks.models[modelId]?.riskFormData?.[riskArea]?.riskSources?.find(
        (r) => r.name === source.name,
      );
      // Create isolated copy to prevent shared references
      return src?.causes ? JSON.parse(JSON.stringify(src.causes)) : {};
    }
  );
  const answers = useMemo(() => rawAnswers, [rawAnswers]);

  /* ── persist helper with data isolation ─────────────────── */
  const persist = useCallback(
    (blob) => {
      dispatch(
        saveCauses({
          modelId,
          assessmentUuid,
          risk_area: riskArea,
          risk_source: source.name,
          source_value: source.name,
          // Create isolated copy to prevent unintended mutations during save
          causes: JSON.parse(JSON.stringify(blob)),
        }),
      );
    },
    [dispatch, assessmentUuid, modelId, riskArea, source.name],
  );

  /* ── callbacks ─────────────────────────────────────────── */
  const handleEval = useCallback(
    (causeCategory, crit, val) => {
      dispatch(
        setRootCause({
          modelId,
          section: riskArea,
          source: source.name,
          valueKey: null, // flattened
          path: [causeCategory, 'evaluation', crit],
          newValue: val,
        }),
      );

      const src = store
        .getState()
        .risks.models[modelId]?.riskFormData?.[riskArea]?.riskSources?.find(
          (r) => r.name === source.name,
        );
      const blob = src?.causes ? JSON.parse(JSON.stringify(src.causes)) : {};
      persist(blob);
    },
    [dispatch, modelId, riskArea, source.name, persist, store],
  );

  const handleDeepValues = useCallback(
    (causeKey, values) => {
      dispatch(
        setDeepDiveValues({
          modelId,
          section: riskArea,
          source: source.name,
          valueKey: null,
          causeCategory: causeKey,
          deepValues: values,
        }),
      );
      const src = store
        .getState()
        .risks.models[modelId]?.riskFormData?.[riskArea]?.riskSources?.find(
          (r) => r.name === source.name,
        );
      const blob = src?.causes ? JSON.parse(JSON.stringify(src.causes)) : {};
      persist(blob);
    },
    [dispatch, modelId, riskArea, source.name, persist, store],
  );

  const handleDeepDetail = useCallback(
    (causeKey, option, detail) => {
      dispatch(
        setDeepDiveDetail({
          modelId,
          section: riskArea,
          source: source.name,
          valueKey: null,
          causeCategory: causeKey,
          option,
          detail,
        }),
      );
      const src = store
        .getState()
        .risks.models[modelId]?.riskFormData?.[riskArea]?.riskSources?.find(
          (r) => r.name === source.name,
        );
      const blob = src?.causes ? JSON.parse(JSON.stringify(src.causes)) : {};
      persist(blob);
    },
    [dispatch, modelId, riskArea, source.name, persist, store],
  );

  /* ── completeness check ───────────────────────────────── */
  const evaluationDone = useMemo(() => {
    // Case-insensitive check for "other"
    const critList = Object.keys(evalTemplate).filter(
      (k) => k.trim().toLowerCase() !== 'other'
    );
    return Object.keys(causesTemplate).some((cat) => {
      const ev = answers[cat]?.evaluation || {};
      return critList.every((crit) => ev[crit]?.value);
    });
  }, [answers, causesTemplate, evalTemplate]);

  /* ── render ────────────────────────────────────────────── */
  return (
    <Container fluid px="xl" py="xl">
      {/* Header */}
      <Stack gap="md" mb="xl">
  <Title order={3} c="dark.7">
    Identify Root Causes
  </Title>
  
  <Text size="md" c="dimmed">
    Evaluate the potential root causes for this risk source, and define the entities involved (who?), timing (when?) and their intent (why?) that leads to a risk. You will identify risk scenarios in the next step.  
  </Text>
  
  <Group gap="md">
    <Text size="md" c="dimmed">
      Identifying root causes for:
    </Text>
    <Badge 
      variant="filled"
      size="lg"
      radius="sm"
      sx={(theme) => ({
        backgroundColor: theme.colors.indigo[5],
        color: theme.white,
      })}
    >
      {source.name}
    </Badge>
  </Group>
</Stack>

      {/* Cause Analysis Table */}
      <Paper
        withBorder
        radius="md"
         p="md"
        mb="xl"
        sx={(theme) => ({
          border: `1px solid ${theme.colors.gray[2]}`,
          overflow: 'hidden',
        })}
      >
        <CauseTable
          causeTemplate={causesTemplate}
          evalTemplate={evalTemplate}
          answers={answers}
          onEvalChange={handleEval}
          onDeepValuesChange={handleDeepValues}
          onDeepDetailChange={handleDeepDetail}
        />
      </Paper>

      {/* Progress Indicator */}
      {!evaluationDone && (
        <Alert 
          icon={<IconAlertCircle size={16} />}
          color="yellow"
          variant="light"
          radius="md"
          mb="xl"
          sx={(theme) => ({
            backgroundColor: theme.colors.yellow[0],
            borderColor: theme.colors.yellow[3],
          })}
        >
          <Text size="sm" fw={500}>
            Complete the evaluation for at least one cause to continue.
          </Text>
        </Alert>
      )}

      {/* Navigation */}
      <Navigation
        onBack={onBack}
        onNext={onNext}
        nextLabel="Continue to Risk Identification"
        nextDisabled={!evaluationDone}
        showBack={!!onBack}
      />
    </Container>
  );
}
